import { useCallback, useMemo } from 'react';
import { useLedger } from '../data/useLedger';
import { useStudents } from '../data/useStudents';
import { useEvents } from '../data/useEvents';
import { useCategories } from '../data/useCategories';
import useNames from './useNames';

// Who a line on an invoice is for, and where the lesson happened.
//
// Every line names the student it is for (#633), and two of the Accounts &
// Invoices settings — grouping by student, showing the room — need facts an
// invoice line does not carry as words. `buildInvoice` stores a line as { id,
// date, description, kind, amountMinor, signedMinor, sourceType, studentId } —
// the student's id, never their name, and no location — because the snapshot
// exists so the money cannot move under a family's feet, and neither of these
// is money. (A frozen invoice, #622, is drawn from its frozen body, where the
// names are written; this is for the ones that are not.)
//
// So they are looked up live, through the chain the ids provide:
//
//   line.id  is the ledger entry's id — attendance writes a charge with a
//            derived id (core/src/ids.rs) and buildInvoice passes it through, so
//            the entry is a map lookup rather than an id parse.
//   entry.studentId    who the charge is for.
//   entry.sourceId     the occurrence the charge came from, for a line with
//                      sourceType 'lesson'.
//   occurrence.locationId → the location's own label.
//
// What that trades:
//
//   * A room renamed after the invoice was issued shows its current name: the
//     family is being told where to turn up.
//   * It works on invoices issued before either setting existed.
//   * Where the lookup comes up empty — a deleted student, an occurrence whose
//     series has since been removed, the "(Balance Forward)" row, which is not
//     an entry at all — the line renders without the detail.
//
// On invoices condensed before #633, the Condensed style collapsed lines that
// shared a description, amount and kind, keeping the first member's id, so
// two students on identical lessons became one line attributed to the first.
// `condense` in domain/invoice.js keys on the line's student too; an invoice
// condensed before then is drawn as it was issued.
//
// The occurrence lookup is bounded by the invoice's own period: expanding an
// indefinite weekly series over all time is the cost domain/reports.js
// records.
export default function useInvoiceLineDetail(invoice) {
  const { entries } = useLedger();
  const { getStudent } = useStudents();
  const { occurrencesBetween } = useEvents();
  const { locations } = useCategories();
  const names = useNames();

  const entriesById = useMemo(() => {
    const map = new Map();
    for (const e of entries) map.set(e.id, e);
    return map;
  }, [entries]);

  const locationLabels = useMemo(() => {
    const map = new Map();
    for (const l of locations) map.set(l.id, l.label);
    return map;
  }, [locations]);

  const start = invoice?.periodStart || null;
  const end = invoice?.periodEnd || null;

  const occurrencesById = useMemo(() => {
    const map = new Map();
    if (!start || !end) return map;
    for (const o of occurrencesBetween(start, end)) map.set(o.id, o);
    return map;
  }, [occurrencesBetween, start, end]);

  return useCallback((line) => {
    const entry = entriesById.get(line?.id);
    if (!entry) return { studentName: null, location: null };

    const student = entry.studentId ? getStudent(entry.studentId) : null;
    const occurrence = entry.sourceType === 'lesson' && entry.sourceId
      ? occurrencesById.get(entry.sourceId)
      : null;

    return {
      // The Student Name Format, like every other name in the app.
      studentName: student ? names.student(student) : null,
      location: occurrence?.locationId ? locationLabels.get(occurrence.locationId) || null : null,
    };
  }, [entriesById, occurrencesById, locationLabels, getStudent, names]);
}
